import { CellMessage, pxToRem } from '@fluentsms/agentnet-web-components';
import { Box, IconButton, Menu, MenuItem, Tooltip, Typography } from '@material-ui/core';
import MuiAccordion from '@material-ui/core/Accordion';
import MuiAccordionDetails from '@material-ui/core/AccordionDetails';
import MuiAccordionSummary from '@material-ui/core/AccordionSummary';
import { createStyles, makeStyles, Theme, withStyles } from '@material-ui/core/styles';
import {
  BusinessOutlined,
  DescriptionOutlined,
  ExpandMore,
  ListAltOutlined,
  MoreHoriz,
  PeopleAltOutlined,
  PersonOutlined,
  PlaceOutlined,
} from '@material-ui/icons';
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined';
import EditIcon from '@material-ui/icons/Edit';
import clsx from 'clsx';
import OrderInsightsRiskIcon from 'features/files/order-insights/components/OrderInsightsRiskIcon';
import { ChangeEvent, MouseEvent, ReactElement, useEffect, useState } from 'react';
import PDFIcon from 'ui-kit/icons/PDF';
import { VerifiedUserOutlined, ErrorOutlineOutlined } from '@material-ui/icons';
import { HelpOutlined } from '../../icons/AgentNetIcon';
import AgentNetButton from '../button/AgentNetButton';

export interface AccordionContentProps {
  id: number | string;
  currentIndex?: number;
  coveredPartyId?: number;
  title: string;
  subtitle?: any[];
  hideSubtitle?: boolean;
  createdDate?: string;
  icon?: string;
  messageIcon?: string;
  orderInsightsRiskSeverity?: string;
  children?: ReactElement;
  expanded?: boolean;
  expandhide?: boolean;
  closeAll?: boolean;
  className?: string;
  sdnMatch?: boolean;
  isEditing?: boolean;
  isEditDisabled?: boolean;
  isVoidDisabled?: boolean;
  isUnvoidDisabled?: boolean;
  isPdfDisabled?: boolean;
  isDeleteDisabled?: boolean;
  isGeneratePdfDisabled?: boolean;
  isTitleMonitoringDisabled?: boolean;
  type?: string;
  status?: string;
  onGenerateReport?: (propertyIndex: number) => void;
  onTitleMonitoring?: (propertyIndex: number) => void;
  onValidateAddress?: (propertyIndex: number) => void;
  onDelete?: (id: number | string, coveredPartyId?: number) => void;
  onEdit?: (id: number | string, coveredPartyId?: number) => void;
  onVoid?: (id: number | string, coveredPartyId?: number) => void;
  onPDF?: (id: number | string) => void;
  onMore?: (id: number | string) => void;
  onMoreLabel?: string;
  onExpandChange?: (expand: boolean, id: number | string) => void;
  onMessage?: (id: number | string) => void;
  onUnvoid?: (id: number | string, coveredPartyId?: number) => void;
  generateReportQAAttribute?: string;
  titleMonitoringQAAttribute?: string;
  titleMonitoringDisabledTooltip?: string;
  validateQAAttribute?: string;
  deleteQAAttribute?: string;
  voidQAAttribute?: string;
  voidText?: string;
  unvoidQAAttribute?: string;
  editQAAttribute?: string;
  pdfQAAttribute?: string;
  messageQAAttribute?: string;
  sdnQAAttribute?: string;
  accordianQAAttribute?: string;
  hideStatus?: boolean;
  openSdnPDF?: () => void;
  removeAsterisk?: boolean;
  onCustom?: (id: number | string) => void;
  onCustomLabel?: string;
  onCustomDisabled?: boolean;
  customQAAttribute?: string;
  isCollapseDisabled?: boolean;
  onShiftClickExpand?: () => void;
  accordionRef?: React.RefObject<HTMLDivElement> | null;
  fincenHeaderVariant?: 'primary' | 'light';
}

const Accordion = withStyles((theme) => ({
  root: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: 'none',
    '&:not(:last-child)': {
      borderBottom: 0,
    },
    '&:before': {
      display: 'none',
    },
    '&$expanded': {
      margin: 'auto',
    },
  },
  expanded: {},
}))(MuiAccordion);

const AccordionSummary = withStyles((theme) => ({
  root: {
    transition: '.1s all',
    borderRadius: theme.shape.borderRadius,
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: -1,
    padding: 0,
    '&$expanded': {
      backgroundColor: theme.palette.accordion.activeBackground,
    },
  },
  content: {
    margin: theme.spacing(1, 2, 1, 1),
    width: '100%',
    '&$expanded': {
      '& > .MuiSvgIcon-root': {
        transform: 'translateY(0px)',
      },
    },
    '&.Mui-expanded': {
      margin: theme.spacing(1, 2, 1, 1),
    },
  },
  expanded: {
    borderBottomLeftRadius: '0',
    borderBottomRightRadius: '0',
    backgroundColor: theme.palette.accordion.activeBackground,
  },
}))(MuiAccordionSummary);

const AccordionDetails = withStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
}))(MuiAccordionDetails);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    summaryWrap: {
      display: 'flex',
      alignItems: 'center',
    },
    iconWrap: {
      backgroundColor: 'transparent',
      borderRadius: theme.shape.borderRadius,
      width: '40px',
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: `0 ${theme.spacing(1)}px`,
      '& .MuiSvgIcon-root': {
        fill: theme.palette.common.white,
      },
    },
    underwritingType: {
      backgroundColor: theme.palette.accordion.document,
    },
    propertyType: {
      backgroundColor: theme.palette.accordion.property,
    },
    buyerType: {
      backgroundColor: theme.palette.accordion.buyer,
    },
    sellerType: {
      backgroundColor: theme.palette.accordion.seller,
    },
    lenderType: {
      backgroundColor: theme.palette.accordion.lender,
    },
    statusDraft: {
      color: theme.palette.text.secondary,
    },
    statusOpen: {
      color: '#43a047', // green/600
    },
    statusPending: {
      color: '#AD5F00',
    },
    statusExamining: {
      color: '#fb8c00', //orange/600
    },
    statusError: {
      color: '#e53935', // red/600
    },
    statusDelivered: {
      color: '#00897B',
    },
    statusRequested: {
      color: '#00ACC1',
    },
    statusCancelled: {
      color: '#5E35B1',
    },
    statusClosed: {
      color: '#013A6F',
    },
    statusVoid: {
      color: '#4F4178',
    },
    statusReported: {
      color: '#48799C',
    },
    statusSearching: {
      color: '#1e88e5', // blue/600
    },
    statusOrderPlaced: {
      color: '#546E7A',
    },
    serviceOrderType: {
      backgroundColor: theme.palette.accordion.document,
    },
    cplDraft: {
      backgroundColor: '#666',
    },
    cplOpen: {
      backgroundColor: '#056B2E',
    },
    cplPending: {
      backgroundColor: '#AD5F00',
    },
    cplError: {
      backgroundColor: '#8F0E0E',
    },
    cplCancelled: {
      backgroundColor: '#9E9E9E',
    },
    cplClosed: {
      backgroundColor: '#013A6F',
    },
    cplVoid: {
      backgroundColor: '#4F4178',
    },
    cplReported: {
      backgroundColor: '#48799C',
    },
    undefinedIcon: {
      backgroundColor: theme.palette.status.draft,
    },
    truncate: {
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      minHeight: '21px',
      maxWidth: '500px',
    },
    subtitleWrap: {
      display: 'flex',
      alignItems: 'center',
    },
    subtitle: {
      color: theme.palette.text.secondary,
      marginRight: theme.spacing(2),
      maxWidth: '350px',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
    },
    titleWrap: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      margin: `0 ${theme.spacing(1)}px`,
      height: '45px', // to match figma
    },
    summaryActions: {
      marginLeft: 'auto',
      display: 'flex',
      alignItems: 'center',
      '& .MuiSvgIcon-root': {
        fontSize: pxToRem(21),
      },
    },
    summaryActionBtn: {
      marginLeft: theme.spacing(1.5),
    },
    summaryActionBtnEdit: {
      marginLeft: theme.spacing(1.5),
      '&.Mui-disabled': {
        background: 'transparent',
        borderColor: 'transparent',
        color: theme.palette.text.disabled,
      },
    },
    fileMessageBtn: {
      padding: 0,
    },
    generateReportBtn: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: '10px',
      alignItems: 'center',
    },
    expandIcon: {
      margin: theme.spacing(1),
      transform: 'rotate(-90deg)',
      transition: `${theme.transitions.duration.shortest}ms all`,
      fill: theme.palette.actionSecondary.active,
      alignSelf: 'center',
    },
    sdnResult: {
      display: 'flex',
      alignItems: 'center',
      lineHeight: 1,
    },
    isMatched: {
      color: theme.palette.error.main,
      fill: theme.palette.error.main,
    },
    sdnBtn: {
      marginRight: theme.spacing(2),
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    noMatch: {
      color: theme.palette.success.main,
      fill: theme.palette.success.main,
    },
    isEditing: {
      backgroundColor: `${theme.palette.accordion.editBackground} !important`,
      '& .MuiAccordionSummary-content': {
        backgroundColor: 'transparent',
      },
    },
    status: {
      marginRight: theme.spacing(0.5),
    },
    statusText: {
      marginRight: theme.spacing(2),
    },
    cplContainer: {
      flexDirection: 'column',
    },
    titleOnly: {
      lineHeight: pxToRem(38),
    },
    selectableText: {
      userSelect: 'text',
      cursor: 'text',
    },
    orderInsightsRisk: {
      marginRight: theme.spacing(2),
    },
    iconBtn: {
      minWidth: 40,
      transition: '.1s all',
      '&:hover': {
        '& svg': {
          fill: theme.palette.secondary.dark,
        },
      },
    },
    statusDot: {
      fontSize: pxToRem(24),
      verticalAlign: 'middle',
      display: 'inline-block',
      lineHeight: 1,
      position: 'relative',
    },
    svgBtn: {
      marginLeft: theme.spacing(1.5),
      minWidth: 'auto',
      height: 32,
      '& svg': {
        width: 18,
        height: 'auto',
      },
    },
  }),
);

export default function AccordionContent({
  title = '8 Elderberry Street, Irvine, CA 92603',
  subtitle = ['Residential (1-4 Family)', 'APN: 931-676-91', 'Orange County'],
  hideSubtitle = false,
  createdDate,
  icon,
  messageIcon,
  orderInsightsRiskSeverity,
  children,
  expanded,
  expandhide,
  closeAll,
  className,
  id,
  currentIndex,
  sdnMatch,
  isEditing,
  isEditDisabled,
  isUnvoidDisabled,
  isVoidDisabled,
  isPdfDisabled,
  isDeleteDisabled,
  isGeneratePdfDisabled,
  isTitleMonitoringDisabled,
  type,
  status,
  onGenerateReport,
  onTitleMonitoring,
  onValidateAddress,
  onDelete,
  onEdit,
  onVoid,
  onUnvoid,
  onPDF,
  onMore,
  onMoreLabel,
  onExpandChange,
  onMessage,
  generateReportQAAttribute,
  titleMonitoringQAAttribute,
  titleMonitoringDisabledTooltip,
  validateQAAttribute,
  deleteQAAttribute,
  voidQAAttribute,
  voidText,
  unvoidQAAttribute,
  editQAAttribute,
  messageQAAttribute,
  pdfQAAttribute,
  sdnQAAttribute,
  accordianQAAttribute,
  hideStatus,
  openSdnPDF,
  removeAsterisk,
  onCustom,
  onCustomLabel,
  onCustomDisabled,
  customQAAttribute,
  isCollapseDisabled,
  onShiftClickExpand,
  coveredPartyId,
  accordionRef,
}: AccordionContentProps): JSX.Element {
  const classes = useStyles();
  const [expand, setExpanded] = useState<boolean>(expanded ? expanded : false);
  const handleChange = (event: MouseEvent) => {
    if (!isCollapseDisabled) {
      if (event.shiftKey && onShiftClickExpand) {
        onShiftClickExpand();
      } else {
        setExpanded(!expand);
      }
    }
  };

  useEffect(() => {
    expanded !== undefined && setExpanded(expanded);
  }, [expanded, closeAll]);

  useEffect(() => {
    onExpandChange?.(expand, coveredPartyId ? `${id}${coveredPartyId}` : id);
  }, [expand]);

  useEffect(() => {
    isEditing && setExpanded(true);
  }, [isEditing]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = () => {
    onDelete?.(id);
    handleMenuClose();
  };

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      id={`more-menu-${id}`}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem onClick={handleDelete}>{onMoreLabel}</MenuItem>
    </Menu>
  );

  const removeAsteriskFn = (title: string) => {
    const i = title.indexOf('*');
    if (i !== -1) {
      return title.slice(0, i) + title.slice(i + 1);
    }
    return title;
  };

  const expandIcon = <ExpandMore className={classes.expandIcon} visibility={expandhide ? 'hidden' : 'visible'} />;

  return (
    <div className={className}>
      <Accordion
        square
        expanded={expand}
        elevation={0}
        onChange={(event: ChangeEvent<unknown>, isExpanded: boolean) => {
          if ((event.target as HTMLElement).tagName !== 'BUTTON') {
            handleChange(event as unknown as MouseEvent);
          }
        }}
      >
        <AccordionSummary
          aria-controls={`panel-${id}`}
          id={`panel-${id}`}
          {...(accordianQAAttribute ? { 'data-testid': accordianQAAttribute } : {})}
          className={isEditing ? classes.isEditing : ''}
        >
          {onShiftClickExpand ? (
            <Tooltip title={`Shift + Left Click to ${expand ? 'collapse' : 'expand'} all`} enterDelay={2000}>
              {expandIcon}
            </Tooltip>
          ) : (
            expandIcon
          )}
          <div className={classes.summaryWrap}>
            {type && (
              <div
                className={clsx(classes.iconWrap, {
                  [classes.underwritingType]: type === 'underwriting',
                  [classes.serviceOrderType]: type === 'orders',
                  [classes.propertyType]: type === 'property',
                  [classes.buyerType]: type === 'buyer' || type === 'Buyer/Borrower',
                  [classes.sellerType]: type === 'seller' || type === 'Seller/Owner',
                  [classes.lenderType]: type === 'lender' || type === 'Lender',
                  [classes.undefinedIcon]: type === 'unknown',
                  [classes.cplDraft]: status === 'Draft',
                  [classes.cplOpen]: status === 'Open',
                  [classes.cplError]: status === 'Error' || status === 'Opened In Error',
                  [classes.cplPending]: status === 'Pending' || status === 'Pending Remit' || status === 'Pending Void',
                  [classes.cplCancelled]: status === 'Cancelled',
                  [classes.cplClosed]: status === 'Closed',
                  [classes.cplVoid]: status === 'Void',
                  [classes.cplReported]: status === 'Reported',
                  [classes.cplReported]: status === 'Remitted' || status === 'Reported',
                })}
              >
                {icon === 'property' && <PlaceOutlined />}
                {icon === 'Individual' && <PersonOutlined />}
                {icon === 'A Married Couple' && <PeopleAltOutlined />}
                {icon === 'Entity' && <BusinessOutlined />}
                {icon === 'Trust/Estate' && <BusinessOutlined />}
                {icon === 'Lender' && <BusinessOutlined />}
                {icon === 'unknown' && <HelpOutlined />}
                {icon === 'document' && <DescriptionOutlined />}
                {icon === 'list' && <ListAltOutlined />}
                {messageIcon == 'message' && <CellMessage />}
              </div>
            )}
            <div className={classes.titleWrap}>
              <Typography
                variant="h5"
                className={clsx(classes.truncate, { [classes.titleOnly]: hideSubtitle })}
                color="textPrimary"
              >
                <span onClick={(e) => e.stopPropagation()} className={classes.selectableText}>
                  {removeAsterisk ? removeAsteriskFn(title) : title}
                </span>
              </Typography>
              {hideSubtitle === false && (
                <div className={classes.subtitleWrap}>
                  {!hideStatus && status && (
                    <Typography
                      variant="body2"
                      className={clsx(classes.status, {
                        [classes.statusDraft]: status === 'Draft',
                        [classes.statusOpen]:
                          status === 'Open' || status === 'Order Placed' || status === 'Quality Review',
                        [classes.statusError]:
                          status === 'Error' || status === 'Opened In Error' || status === 'Action Required',
                        [classes.statusPending]:
                          status === 'Pending' || status === 'Pending Remit' || status === 'Pending Void',
                        [classes.statusExamining]: status === 'Examining',
                        [classes.statusCancelled]: status === 'Cancelled',
                        [classes.statusClosed]: status === 'Closed',
                        [classes.statusVoid]: status === 'Void',
                        [classes.statusReported]: status === 'Reported',
                        [classes.statusSearching]: status === 'Searching',
                        [classes.statusOrderPlaced]: status === 'Order Placed',
                        [classes.statusRequested]:
                          status === 'Update Requested' ||
                          status === 'Amend Requested' ||
                          status === 'Date Down Requested',
                        [classes.statusDelivered]:
                          status === 'Update Delivered' ||
                          status === 'Amend Delivered' ||
                          status === 'Date Down Delivered' ||
                          status === 'Order Delivered' ||
                          status === 'Delivered',
                      })}
                    >
                      <span className={classes.statusDot}>&#x25cf;</span>
                    </Typography>
                  )}
                  {!hideStatus && status && (
                    <Typography variant="body2" className={classes.statusText}>
                      {status}
                    </Typography>
                  )}

                  {sdnMatch === true && (
                    <AgentNetButton
                      variant="text"
                      className={classes.sdnBtn}
                      size="small"
                      startIcon={<ErrorOutlineOutlined color="error" className={classes.isMatched} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        openSdnPDF && openSdnPDF();
                      }}
                    >
                      <Typography
                        variant="h5"
                        className={clsx(classes.sdnResult, classes.isMatched)}
                        data-testid={sdnQAAttribute + 'Match'}
                      >
                        SDN Match
                      </Typography>
                    </AgentNetButton>
                  )}
                  {sdnMatch === false && (
                    <AgentNetButton
                      variant="text"
                      className={classes.sdnBtn}
                      size="small"
                      startIcon={<VerifiedUserOutlined color="primary" className={classes.noMatch} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        openSdnPDF && openSdnPDF();
                      }}
                    >
                      <Typography
                        variant="h5"
                        className={clsx(classes.sdnResult, classes.noMatch)}
                        data-testid={sdnQAAttribute + 'NoMatch'}
                      >
                        SDN
                      </Typography>
                    </AgentNetButton>
                  )}
                  {subtitle.length ? (
                    <>
                      {orderInsightsRiskSeverity && (
                        <span className={classes.orderInsightsRisk}>
                          <OrderInsightsRiskIcon riskSeverity={orderInsightsRiskSeverity} />{' '}
                        </span>
                      )}
                      {subtitle.map((e) => (
                        <Typography variant="body2" key={e} className={classes.subtitle}>
                          <span onClick={(e) => e.stopPropagation()} className={classes.selectableText}>
                            {e}
                          </span>
                        </Typography>
                      ))}
                    </>
                  ) : (
                    <Typography variant="body2" className={classes.subtitle}>
                      -
                    </Typography>
                  )}
                  {createdDate ? (
                    <Typography variant="body2" className={classes.subtitle}>
                      <span onClick={(e) => e.stopPropagation()} className={classes.selectableText}>
                        {`Created: ${createdDate}`}
                      </span>
                    </Typography>
                  ) : null}
                </div>
              )}
            </div>
          </div>
          <div className={classes.summaryActions}>
            {onEdit && (
              <AgentNetButton
                variant="outlined"
                color="primary"
                size="small"
                className={classes.svgBtn}
                disabled={isEditDisabled ? isEditDisabled : isEditing}
                onClick={(e) => {
                  onEdit(id, coveredPartyId);
                }}
                {...(editQAAttribute ? { 'data-testid': editQAAttribute } : {})}
              >
                {isEditing ? 'Editing' : <EditIcon fontSize="small" />}
              </AgentNetButton>
            )}
            {onVoid && (
              <AgentNetButton
                variant="outlined"
                color="primary"
                size="small"
                className={classes.summaryActionBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  onVoid(id, coveredPartyId);
                }}
                disabled={isVoidDisabled}
                {...(voidQAAttribute ? { 'data-testid': voidQAAttribute } : {})}
              >
                {voidText || 'Void'}
              </AgentNetButton>
            )}
            {onUnvoid && (
              <AgentNetButton
                variant="outlined"
                color="primary"
                size="small"
                className={classes.summaryActionBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  onUnvoid(id, coveredPartyId);
                }}
                disabled={isUnvoidDisabled && isUnvoidDisabled}
                {...(unvoidQAAttribute ? { 'data-testid': unvoidQAAttribute } : {})}
              >
                Unvoid
              </AgentNetButton>
            )}
            {onTitleMonitoring && (
              /* Disabled buttons swallow pointer events, so the tooltip anchors to the wrapper instead */
              <Tooltip
                title={isTitleMonitoringDisabled ? titleMonitoringDisabledTooltip ?? '' : ''}
                arrow
                placement="top"
              >
                <div className={classes.summaryActionBtn}>
                  <AgentNetButton
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onTitleMonitoring(currentIndex ?? 0);
                    }}
                    disabled={isTitleMonitoringDisabled}
                    startIcon={<PDFIcon disabled={isTitleMonitoringDisabled} />}
                    {...(titleMonitoringQAAttribute ? { 'data-testid': titleMonitoringQAAttribute } : {})}
                  >
                    Title Monitoring
                  </AgentNetButton>
                </div>
              </Tooltip>
            )}
            {onGenerateReport && (
              <Tooltip
                title={isGeneratePdfDisabled ? 'Property report not available for this address.' : ''}
                arrow
                placement="top"
              >
                <div className={classes.summaryActionBtn}>
                  <AgentNetButton
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onGenerateReport(currentIndex ?? 0);
                    }}
                    disabled={isGeneratePdfDisabled}
                    startIcon={<PDFIcon disabled={isGeneratePdfDisabled} />}
                    {...(generateReportQAAttribute ? { 'data-testid': generateReportQAAttribute } : {})}
                  >
                    <span className={classes.generateReportBtn}>Property Report</span>
                  </AgentNetButton>
                </div>
              </Tooltip>
            )}
            {onValidateAddress && (
              <AgentNetButton
                variant="outlined"
                color="primary"
                size="small"
                className={classes.summaryActionBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  onValidateAddress(currentIndex ?? 0);
                }}
                {...(validateQAAttribute ? { 'data-testid': validateQAAttribute } : {})}
              >
                Validate Address
              </AgentNetButton>
            )}
            {onCustom && (
              <AgentNetButton
                variant="outlined"
                color="primary"
                size="small"
                className={classes.summaryActionBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  onCustom(id);
                }}
                disabled={onCustomDisabled}
                {...(customQAAttribute ? { 'data-testid': customQAAttribute } : {})}
              >
                {onCustomLabel ? onCustomLabel : 'Action'}
              </AgentNetButton>
            )}
            {onPDF && (
              <AgentNetButton
                variant="outlined"
                color="primary"
                size="small"
                className={classes.svgBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  onPDF(id);
                }}
                disabled={isPdfDisabled && isPdfDisabled}
                {...(pdfQAAttribute ? { 'data-testid': pdfQAAttribute } : {})}
              >
                <PDFIcon />
              </AgentNetButton>
            )}
            {onMessage && (
              <AgentNetButton
                variant="outlined"
                color="primary"
                size="small"
                className={clsx(classes.fileMessageBtn, classes.svgBtn)}
                onClick={(e) => {
                  e.stopPropagation();
                  onMessage(id);
                }}
                disabled={isPdfDisabled && isPdfDisabled}
                {...(messageQAAttribute ? { 'data-testid': messageQAAttribute } : {})}
              >
                <CellMessage />
              </AgentNetButton>
            )}
            {onDelete && (
              <AgentNetButton
                variant="text"
                size="small"
                className={clsx(classes.summaryActionBtn, classes.iconBtn)}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(id, coveredPartyId);
                }}
                disabled={isDeleteDisabled && isDeleteDisabled}
                {...(deleteQAAttribute ? { 'data-testid': deleteQAAttribute } : {})}
              >
                <DeleteOutlineOutlinedIcon color={isDeleteDisabled ? 'disabled' : 'secondary'} />
              </AgentNetButton>
            )}
            {onMore && (
              <Box p={1}>
                <IconButton
                  disableFocusRipple
                  disableRipple
                  disableTouchRipple
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuOpen(e);
                  }}
                  onFocus={(e) => e.stopPropagation()}
                >
                  <MoreHoriz />
                </IconButton>
                {renderMenu}
              </Box>
            )}
          </div>
        </AccordionSummary>
        <AccordionDetails ref={accordionRef}>{children}</AccordionDetails>
      </Accordion>
    </div>
  );
}
